import {
  Button,
  Card,
  Group,
  MultiSelect,
  Paper,
  SegmentedControl,
  Skeleton,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { LineChart } from "@mantine/charts";
import { useEffect, useMemo, useState } from "react";
import { formatBucketChartLabel } from "@mip-tg-bot-turntable/utils";
import { adminErrText, AdminApiError } from "../../../api.ts";
import { BotTimezoneNotice } from "../../../components/BotTimezoneNotice.tsx";
import { AdminTable } from "../../../components/AdminTable.tsx";
import { TablePagination } from "../../../components/TablePagination.tsx";
import { useClientPagination } from "../../../hooks/useClientPagination.ts";
import { AnalyticsStatsFilters } from "./AnalyticsStatsFilters.tsx";
import {
  CHART_SERIES_TOP_N,
  CHAT_LINE_COLORS,
  chatSeriesKey,
  STATS_TABLE_PAGE_SIZE,
  type ChatFunnelMetric,
  type ChatFunnelSeries,
} from "./stats-shared.ts";
import { CHAT_FUNNEL_SECTIONS, useAnalyticsStats } from "./useAnalyticsStats.ts";

type Props = {
  readonly botId: string;
};

/** 图例超过此数量隐藏（防 200+ 群撑裂） */
const LEGEND_MAX_SERIES = 16;

/**
 * 已选超过此数量时不再渲染全部 pill（只显示「已选 N 个」摘要 + 下拉可改）。
 * pill 全量堆叠会把 MultiSelect 高度撑到数万 px。
 */
const COMPACT_PICK_THRESHOLD = 8;

/** 稳定空数组，避免 `?? []` 每帧新引用触发 useEffect 死循环 (React #185) */
const EMPTY_CHAT_SERIES: ReadonlyArray<ChatFunnelSeries> = [];

function metricValue(
  metric: ChatFunnelMetric,
  pt: { visits: number; joined: number; sent: number; skipped?: number } | undefined,
): number {
  if (pt === undefined) return 0;
  if (metric === "sent") return pt.sent;
  if (metric === "joined") return pt.joined;
  if (metric === "skipped") return pt.skipped ?? 0;
  return pt.visits;
}

function totalForMetric(s: ChatFunnelSeries, metric: ChatFunnelMetric): number {
  if (metric === "sent") return s.totals.sent;
  if (metric === "joined") return s.totals.joined;
  if (metric === "skipped") return s.totals.skipped ?? 0;
  return s.totals.visits;
}

function sameIdList(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

export function ChatFunnelTab({ botId }: Props) {
  const stats = useAnalyticsStats(botId, CHAT_FUNNEL_SECTIONS);
  const { data, isLoading, isError, error, activeGranularity } = stats;
  const [chatMetric, setChatMetric] = useState<ChatFunnelMetric>("sent");
  /** 折线要画的群；空=不画线。首次有数据时自动填 Top N */
  const [pickedChatIds, setPickedChatIds] = useState<string[]>([]);
  /** 是否已对当前数据做过默认 TopN 播种（清空后不强制再种） */
  const [didSeedTop, setDidSeedTop] = useState(false);

  const chatSeries = data?.funnelTrendByChat ?? EMPTY_CHAT_SERIES;

  const ranked = useMemo(() => {
    return [...chatSeries].sort(
      (a, b) => totalForMetric(b, chatMetric) - totalForMetric(a, chatMetric),
    );
  }, [chatSeries, chatMetric]);

  const multiSelectData = useMemo(
    () =>
      ranked.map((s) => ({
        value: s.chatId,
        label:
          s.chatTitle.length > 40
            ? `${s.chatTitle.slice(0, 40)}... (${s.chatId})`
            : `${s.chatTitle} (${s.chatId})`,
      })),
    [ranked],
  );

  const rankedChatIdsKey = useMemo(() => ranked.map((s) => s.chatId).join("\0"), [ranked]);

  // range/metric/filter 变化：允许在选择为空时重新默认 TopN
  useEffect(() => {
    setDidSeedTop(false);
  }, [stats.range, chatMetric, stats.chatId, stats.adId]);

  // 剔除失效 id；首次有群且未手动操作过时默认选 Top N
  useEffect(() => {
    if (ranked.length === 0) {
      setPickedChatIds((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    const alive = new Set(ranked.map((s) => s.chatId));
    const topIds = ranked.slice(0, CHART_SERIES_TOP_N).map((s) => s.chatId);

    setPickedChatIds((prev) => {
      const pruned = prev.filter((id) => alive.has(id));
      if (pruned.length === 0) {
        // didSeedTop=true 表示用户清过空，保持空；否则默认 Top N
        return didSeedTop ? pruned : topIds;
      }
      if (sameIdList(pruned, prev)) return prev;
      return pruned;
    });
  }, [rankedChatIdsKey, ranked, didSeedTop]);

  const seriesForChart = useMemo(() => {
    if (ranked.length === 0 || pickedChatIds.length === 0) return [] as ChatFunnelSeries[];
    const pick = new Set(pickedChatIds);
    // 保持 ranked 顺序（按指标），不是勾选顺序
    return ranked.filter((s) => pick.has(s.chatId));
  }, [ranked, pickedChatIds]);

  const lineChart = useMemo(() => {
    if (seriesForChart.length === 0) {
      return {
        data: [] as Array<Record<string, string | number>>,
        series: [] as Array<{ name: string; label: string; color: string }>,
      };
    }
    const series = seriesForChart.map((s, i) => ({
      name: chatSeriesKey(s.chatId),
      label: s.chatTitle.length > 18 ? `${s.chatTitle.slice(0, 18)}...` : s.chatTitle,
      color: CHAT_LINE_COLORS[i % CHAT_LINE_COLORS.length] ?? "blue.6",
    }));
    const bucketOrder: string[] = [];
    const bucketSeen = new Set<string>();
    for (const s of seriesForChart) {
      for (const p of s.points) {
        if (!bucketSeen.has(p.bucket)) {
          bucketSeen.add(p.bucket);
          bucketOrder.push(p.bucket);
        }
      }
    }
    const dataRows = bucketOrder.map((bucket) => {
      const row: Record<string, string | number> = {
        bucket,
        bucketLabel: formatBucketChartLabel(bucket, activeGranularity),
      };
      for (const s of seriesForChart) {
        const pt = s.points.find((p) => p.bucket === bucket);
        row[chatSeriesKey(s.chatId)] = metricValue(chatMetric, pt);
      }
      return row;
    });
    return { data: dataRows, series };
  }, [seriesForChart, chatMetric, activeGranularity]);

  const {
    page,
    setPage,
    pageSize,
    total,
    totalPages,
    pageItems: pagedRanked,
  } = useClientPagination(
    ranked,
    `${stats.range}|${stats.chatId ?? ""}|${stats.adId ?? ""}|${chatMetric}`,
    STATS_TABLE_PAGE_SIZE,
  );

  const chatMetricLabel =
    chatMetric === "sent"
      ? "已上报(事件)"
      : chatMetric === "joined"
        ? "归因入群(事件)"
        : chatMetric === "skipped"
          ? "上报跳过(事件)"
          : "访问(事件)";

  const showLegend = lineChart.series.length > 0 && lineChart.series.length <= LEGEND_MAX_SERIES;
  /** 多选时隐藏 pill，避免高度爆炸；需单独展示摘要文案 */
  const pickCompact = pickedChatIds.length > COMPACT_PICK_THRESHOLD;

  const selectTopN = () => {
    setDidSeedTop(true);
    setPickedChatIds(ranked.slice(0, CHART_SERIES_TOP_N).map((s) => s.chatId));
  };

  const selectAll = () => {
    setDidSeedTop(true);
    setPickedChatIds(ranked.map((s) => s.chatId));
  };

  const clearPick = () => {
    setDidSeedTop(true); // 阻止 effect 立刻再种 TopN
    setPickedChatIds([]);
  };

  return (
    <Stack gap="md">
      {data !== undefined ? (
        <BotTimezoneNotice
          timezone={data.timezone}
          statsDay={data.statsDay}
          range={data.range}
          windowStartAt={data.windowStartAt}
          windowEndAt={data.windowEndAt}
        />
      ) : null}
      <AnalyticsStatsFilters stats={stats} />

      {isLoading ? (
        <Stack gap="md">
          <Skeleton h={280} radius="md" />
          <Skeleton h={320} radius="md" />
        </Stack>
      ) : isError ? (
        <Text c="red" size="sm" ta="center" py="lg">
          {adminErrText((error as AdminApiError).code)}
        </Text>
      ) : (
        <>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group justify="space-between" mb="sm" wrap="wrap" align="flex-end">
              <Stack gap={2}>
                <Text fw={600} size="lg">
                  分群对比折线 · 一群一条线
                </Text>
                <Text size="xs" c="dimmed">
                  共 {ranked.length} 群 · 当前画 {pickedChatIds.length} 条 · 指标 {chatMetricLabel}
                  。用下方选择框多选，或点快捷按钮。
                </Text>
              </Stack>
              <SegmentedControl
                size="xs"
                value={chatMetric}
                onChange={(v) => setChatMetric(v as ChatFunnelMetric)}
                data={[
                  { value: "sent", label: "已上报(事件)" },
                  { value: "joined", label: "归因入群(事件)" },
                  { value: "skipped", label: "上报跳过(事件)" },
                  { value: "visits", label: "访问(事件)" },
                ]}
              />
            </Group>

            <Stack gap="xs" mb="md">
              <Group gap="xs" wrap="wrap">
                <Button
                  size="sm"
                  variant="light"
                  onClick={selectTopN}
                  disabled={ranked.length === 0}
                >
                  选择 Top {CHART_SERIES_TOP_N}（按{chatMetricLabel}）
                </Button>
                <Button
                  size="sm"
                  variant="light"
                  onClick={selectAll}
                  disabled={ranked.length === 0}
                >
                  选择全部群（{ranked.length}）
                </Button>
                <Button
                  size="sm"
                  variant="default"
                  onClick={clearPick}
                  disabled={pickedChatIds.length === 0}
                >
                  清空选择
                </Button>
              </Group>

              {pickCompact ? (
                <Paper withBorder p="sm" radius="md" bg="gray.0">
                  <Group justify="space-between" wrap="wrap" gap="xs">
                    <Stack gap={2}>
                      <Text size="sm" fw={600}>
                        已选 {pickedChatIds.length} / {ranked.length} 群
                      </Text>
                      <Text size="xs" c="dimmed">
                        摘要模式：不逐个展示标签（避免高度爆炸）。下方搜索框可增删；或点快捷按钮。
                      </Text>
                    </Stack>
                    <Button size="xs" variant="light" onClick={selectTopN}>
                      改回 Top {CHART_SERIES_TOP_N}
                    </Button>
                  </Group>
                </Paper>
              ) : null}

              <MultiSelect
                label={pickCompact ? "搜索增删群组" : "折线群组"}
                description={
                  pickCompact
                    ? "输入名称搜索，勾选/取消即可调整；当前选中状态见上方摘要。"
                    : "可搜索、多选。快捷：Top 8 / 全部 / 清空。超过 8 个自动进摘要模式。"
                }
                placeholder={
                  ranked.length === 0
                    ? "暂无群组"
                    : pickedChatIds.length === 0
                      ? "未选群，请点「选择 Top 8」或「选择全部群」"
                      : pickCompact
                        ? "输入群名搜索以增删…"
                        : "继续搜索添加群组"
                }
                data={multiSelectData}
                value={pickedChatIds}
                onChange={(ids) => {
                  setDidSeedTop(true);
                  setPickedChatIds(ids);
                }}
                searchable
                clearable
                hidePickedOptions
                nothingFoundMessage="无匹配群组"
                maxDropdownHeight={280}
                disabled={ranked.length === 0}
                // 紧凑：隐藏 pill（有 value 时 placeholder 不显示，所以用上方 Paper 摘要）
                // 非紧凑：pill 区限高滚动
                styles={{
                  input: {
                    minHeight: 36,
                    maxHeight: pickCompact ? 40 : 88,
                    overflowY: "auto",
                    alignContent: "flex-start",
                  },
                  pillsList: pickCompact
                    ? { display: "none" }
                    : {
                        maxHeight: 72,
                        overflowY: "auto",
                        overflowX: "hidden",
                      },
                }}
              />
            </Stack>

            {lineChart.series.length === 0 ? (
              <Text c="dimmed" size="sm" ta="center" py="lg">
                {ranked.length === 0
                  ? "暂无群组数据（请先在「频道/群聊」添加并产生流量）"
                  : "尚未选择群组。请点「选择 Top 8」或「选择全部群」，或在选择框中勾选。"}
              </Text>
            ) : (
              <LineChart
                h={280}
                data={lineChart.data}
                dataKey="bucketLabel"
                withTooltip
                withLegend={showLegend}
                legendProps={showLegend ? { verticalAlign: "bottom", height: 48 } : { height: 0 }}
                tickLine="y"
                curveType="monotone"
                strokeWidth={2}
                dotProps={{ r: activeGranularity === "hour" ? 0 : 2 }}
                activeDotProps={{ r: 4, strokeWidth: 1 }}
                series={lineChart.series}
              />
            )}
            {!showLegend && lineChart.series.length > LEGEND_MAX_SERIES ? (
              <Text size="xs" c="dimmed" mt="xs">
                已画 {lineChart.series.length}{" "}
                条线，图例已隐藏以免撑页；鼠标悬停数据点查看群名与数值。
              </Text>
            ) : null}
          </Card>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text fw={600} size="lg" mb={4}>
              群组排行（本时段汇总）
            </Text>
            <Text size="xs" c="dimmed" mb="md">
              按 {chatMetricLabel} 降序。入群率 = 入群/访问；上报率 = 上报/入群；跳过率 =
              跳过/入群。「加入/移出」同步折线选择。
            </Text>
            {ranked.length === 0 ? (
              <Text c="dimmed" size="sm" ta="center" py="lg">
                暂无排行数据
              </Text>
            ) : (
              <>
                <AdminTable minWidth={840}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 48 }}>#</Table.Th>
                      <Table.Th>群组</Table.Th>
                      <Table.Th>访问(事件)</Table.Th>
                      <Table.Th>归因入群(事件)</Table.Th>
                      <Table.Th>入群用户</Table.Th>
                      <Table.Th>已上报(事件)</Table.Th>
                      <Table.Th>上报跳过</Table.Th>
                      <Table.Th>入群率</Table.Th>
                      <Table.Th>上报率</Table.Th>
                      <Table.Th>跳过率</Table.Th>
                      <Table.Th style={{ width: 88 }}>折线</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {pagedRanked.map((s, idx) => {
                      const rank = (page - 1) * pageSize + idx + 1;
                      const skipped = s.totals.skipped ?? 0;
                      const joinRate =
                        s.totals.visits > 0
                          ? `${((s.totals.joined / s.totals.visits) * 100).toFixed(1)}%`
                          : "-";
                      const reportRate =
                        s.totals.joined > 0
                          ? `${((s.totals.sent / s.totals.joined) * 100).toFixed(1)}%`
                          : "-";
                      const skipRate =
                        s.totals.joined > 0
                          ? `${((skipped / s.totals.joined) * 100).toFixed(1)}%`
                          : "-";
                      const inPick = pickedChatIds.includes(s.chatId);
                      return (
                        <Table.Tr key={s.chatId}>
                          <Table.Td>{rank}</Table.Td>
                          <Table.Td>
                            <Stack gap={0}>
                              <Text size="sm" fw={500} lineClamp={1}>
                                {s.chatTitle}
                              </Text>
                              <Text size="xs" c="dimmed" ff="monospace">
                                {s.chatId}
                              </Text>
                            </Stack>
                          </Table.Td>
                          <Table.Td>{s.totals.visits.toLocaleString()}</Table.Td>
                          <Table.Td>{s.totals.joined.toLocaleString()}</Table.Td>
                          <Table.Td>{(s.totals.joinUsers ?? 0).toLocaleString()}</Table.Td>
                          <Table.Td>{s.totals.sent.toLocaleString()}</Table.Td>
                          <Table.Td>{skipped.toLocaleString()}</Table.Td>
                          <Table.Td>{joinRate}</Table.Td>
                          <Table.Td>{reportRate}</Table.Td>
                          <Table.Td>{skipRate}</Table.Td>
                          <Table.Td>
                            <Button
                              size="compact-xs"
                              variant={inPick ? "filled" : "light"}
                              onClick={() => {
                                setDidSeedTop(true);
                                setPickedChatIds((prev) =>
                                  prev.includes(s.chatId)
                                    ? prev.filter((id) => id !== s.chatId)
                                    : [...prev, s.chatId],
                                );
                              }}
                            >
                              {inPick ? "移出" : "加入"}
                            </Button>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </AdminTable>
                <TablePagination
                  page={page}
                  pageSize={pageSize}
                  total={total}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </>
            )}
          </Card>
        </>
      )}
    </Stack>
  );
}
